// frontend/src/pages/Contratos.js
import React, { useState, useEffect, useCallback } from 'react';
import api from '../api';

const C = {
  navy: '#1A2233', orange: '#D85A30', orangeH: '#B84A24',
  bg: '#f5f7fa', surface: '#ffffff', border: '#d4dde8',
  muted: '#6b7a8d', text: '#1A2233', success: '#27ae60',
  blue: '#0369a1',
};

const MESES = ['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'];
const MESES_FULL = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];

function fmt(v) {
  return Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

// Formata uma data DATE pura (sem hora/fuso, ex: "2026-07-02" ou
// "2026-07-02T00:00:00.000Z" vindo do driver pg) sem deixar o construtor
// Date() converter para o fuso do navegador — isso jogava a data 1 dia para
// trás em fusos negativos como o de Brasília (UTC-3), porque o pg costuma
// entregar colunas DATE como meia-noite UTC.
function fmtData(d) {
  if (!d) return '—';
  const str = typeof d === 'string' ? d : d.toISOString();
  const [ano, mes, dia] = str.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

function fmtDataHora(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' });
}

const PADROES_NFSE_VAZIO = { nfse_nacional: false, codigo_tributacao_nacional_padrao: '', codigo_nbs_padrao: '' };
const soDigitos = v => String(v || '').replace(/\D/g, '');

const inp = {
  width: '100%', boxSizing: 'border-box', padding: '9px 12px',
  border: `1.5px solid ${C.border}`, borderRadius: 8, fontSize: 14,
  fontFamily: 'inherit', color: C.text, outline: 'none', background: '#fff',
};
const lbl = {
  fontSize: 11, fontWeight: 700, color: C.muted,
  textTransform: 'uppercase', letterSpacing: '.5px',
  display: 'block', marginBottom: 4,
};
const btnPrimary = {
  background: C.orange, color: '#fff', border: 'none', borderRadius: 8,
  padding: '10px 22px', fontSize: 14, fontWeight: 700, cursor: 'pointer',
};
const btnSec = {
  background: '#f5f7fa', color: C.text, border: `1px solid ${C.border}`,
  borderRadius: 8, padding: '10px 18px', fontSize: 14, cursor: 'pointer',
};
const card = {
  background: C.surface, border: `1px solid ${C.border}`,
  borderRadius: 12, padding: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
};

function Toggle({ value, onChange, cor }) {
  const bg = value ? (cor || C.success) : C.border;
  return (
    <div onClick={onChange}
      style={{ width: 42, height: 24, borderRadius: 12, position: 'relative', transition: 'background .2s', cursor: 'pointer', background: bg, flexShrink: 0 }}>
      <span style={{ position: 'absolute', top: 3, left: value ? 20 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', transition: 'left .2s', display: 'block', boxShadow: '0 1px 3px rgba(0,0,0,.2)' }} />
    </div>
  );
}

function Badge({ ok }) {
  return (
    <span style={{
      padding: '2px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
      background: ok ? '#dcfce7' : '#fee2e2', color: ok ? '#166534' : '#991b1b',
    }}>
      {ok ? '● Ativa' : '○ Inativa'}
    </span>
  );
}

function StatusBadge({ status }) {
  const map = {
    gerado:     { bg: '#e0f2fe', color: '#0369a1', label: 'Gerado' },
    boleto_ok:  { bg: '#dcfce7', color: '#166534', label: 'Boleto OK' },
    boleto_erro:{ bg: '#fee2e2', color: '#991b1b', label: 'Boleto Erro' },
    nfse_ok:    { bg: '#dcfce7', color: '#166534', label: 'NFS-e OK' },
    nfse_erro:  { bg: '#fee2e2', color: '#991b1b', label: 'NFS-e Erro' },
    pago:       { bg: '#f0fdf4', color: '#15803d', label: 'Pago' },
    cancelado:  { bg: '#f1f5f9', color: '#64748b', label: 'Cancelado' },
  };
  const s = map[status] || { bg: '#f1f5f9', color: '#64748b', label: status };
  return (
    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

function EmailStatusBadge({ status }) {
  const map = {
    enviado:  { bg: '#dcfce7', color: '#166534', label: '✉️ Email enviado' },
    erro:     { bg: '#fee2e2', color: '#991b1b', label: '✉️ Email com erro' },
    pulado:   { bg: '#fef3c7', color: '#92400e', label: '✉️ Email não enviado' },
    pendente: { bg: '#f1f5f9', color: '#64748b', label: '✉️ Email pendente' },
  };
  const s = map[status] || map.pendente;
  return (
    <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>
      {s.label}
    </span>
  );
}

export default function Contratos({ toast }) {
  const [contratos, setContratos] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [carregando, setCarregando] = useState(true);
  const [busca, setBusca] = useState('');
  const [filtroAtivo, setFiltroAtivo] = useState('todos');
  const [modal, setModal] = useState(null);
  const [modalCobrar, setModalCobrar] = useState(null);
  const [modalHistorico, setModalHistorico] = useState(null);
  const [historico, setHistorico] = useState([]);
  const [historicoExpandido, setHistoricoExpandido] = useState(null); // id do histórico aberto
  const [reenviandoId, setReenviandoId] = useState(null); // id do histórico sendo reenviado
  const [salvando, setSalvando] = useState(false);
  const [cobrando, setCobrando] = useState(false);
  const [resultadoCobrar, setResultadoCobrar] = useState(null);

  const mesAtual = new Date().getMonth() + 1;
  const anoAtual = new Date().getFullYear();
  const [cobrarMes, setCobrarMes] = useState(mesAtual);
  const [cobrarAno, setCobrarAno] = useState(anoAtual);

  // Opções editáveis no modal de cobrança manual
  const [cobrarBoleto, setCobrarBoleto] = useState(true);
  const [cobrarNfse, setCobrarNfse] = useState(false);
  const [podeReprocessar, setPodeReprocessar] = useState(false);

  // Padrões de NFS-e da empresa (Ambiente Nacional) — GET /notas/fiscal/nfse-padroes
  const [nfsePadroes, setNfsePadroes] = useState(PADROES_NFSE_VAZIO);
  const [modalPadroes, setModalPadroes] = useState(false);
  const [formPadroes, setFormPadroes] = useState(PADROES_NFSE_VAZIO);
  const [salvandoPadroes, setSalvandoPadroes] = useState(false);
  // Só o gestor edita os padrões (backend também valida o perfil 'ges')
  const [ehGestor] = useState(() => {
    try {
      const u = JSON.parse(localStorage.getItem('usuario') || '{}');
      const perfis = Array.isArray(u.perfis) ? u.perfis : [u.perfil].filter(Boolean);
      return perfis.includes('ges');
    } catch { return false; }
  });

  function calcVencimento(mes, ano, diaVenc) {
    const d = new Date(ano, mes - 1, diaVenc || 10);
    return d.toLocaleDateString('pt-BR');
  }

  const formVazio = {
    nome: '', valor: '', data_inicio: '',
    cliente_id: '', dia_geracao: 1, dia_vencimento: 10, cobranca_ativa: false,
    gerar_boleto: true, emitir_nfse: false,
    item_lista_servico: '', discriminacao: '', observacao: '', ativo: true,
    codigo_tributacao_nacional: '', codigo_nbs: '',
  };
  const [form, setForm] = useState(formVazio);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [cR, clR, pR] = await Promise.all([
        api.get('/contratos'),
        api.get('/orcamentos/clientes/todos'),
        // Falha aqui não impede a tela de abrir — só desliga as dicas de NFS-e Nacional
        api.get('/notas/fiscal/nfse-padroes').catch(() => ({ data: PADROES_NFSE_VAZIO })),
      ]);
      setContratos(cR.data);
      setClientes(clR.data || []);
      setNfsePadroes({ ...PADROES_NFSE_VAZIO, ...(pR.data || {}) });
    } catch { toast('Erro ao carregar contratos.', 'error'); }
    setCarregando(false);
  }, [toast]);

  useEffect(() => { carregar(); }, [carregar]);

  function abrirNovo() {
    setForm(formVazio);
    setModal('novo');
  }

  function abrirEditar(c) {
    setForm({
      nome: c.nome || '',
      valor: c.valor || '',
      data_inicio: c.data_inicio ? c.data_inicio.slice(0, 10) : '',
      cliente_id: c.cliente_id || '',
      dia_geracao: c.dia_geracao || 1,
      dia_vencimento: c.dia_vencimento || 10,
      cobranca_ativa: c.cobranca_ativa || false,
      gerar_boleto: c.gerar_boleto !== false,
      emitir_nfse: c.emitir_nfse || false,
      item_lista_servico: c.item_lista_servico || '',
      discriminacao: c.discriminacao || '',
      observacao: c.observacao || '',
      ativo: c.ativo !== false,
      codigo_tributacao_nacional: c.codigo_tributacao_nacional || '',
      codigo_nbs: c.codigo_nbs || '',
    });
    setModal(c);
  }

  function abrirCobrar(c) {
    setModalCobrar(c);
    setResultadoCobrar(null);
    setPodeReprocessar(false);
    setCobrarMes(mesAtual);
    setCobrarAno(anoAtual);
    // Inicializa com as configurações do contrato
    setCobrarBoleto(c.gerar_boleto !== false);
    setCobrarNfse(c.emitir_nfse || false);
  }

  async function salvar() {
    if (!form.nome.trim()) { toast('Nome é obrigatório.', 'error'); return; }
    const codTrib = soDigitos(form.codigo_tributacao_nacional);
    if (codTrib && codTrib.length !== 6) {
      toast('O Código de Tributação Nacional deve ter 6 dígitos (ex: 010701).', 'error');
      return;
    }
    setSalvando(true);
    try {
      const body = {
        ...form,
        valor: parseFloat(form.valor) || 0,
        codigo_tributacao_nacional: codTrib,
        codigo_nbs: (form.codigo_nbs || '').trim(),
      };
      if (modal === 'novo') {
        await api.post('/contratos', body);
        toast('Contrato criado!', 'success');
      } else {
        await api.patch(`/contratos/${modal.id}`, body);
        toast('Contrato atualizado!', 'success');
      }
      setModal(null);
      carregar();
    } catch (e) { toast(e.response?.data?.erro || 'Erro ao salvar.', 'error'); }
    setSalvando(false);
  }

  async function excluir(c) {
    if (!window.confirm(`Excluir o contrato "${c.nome}"?\n\nEsta ação não pode ser desfeita.`)) return;
    try {
      await api.delete(`/contratos/${c.id}`);
      toast('Contrato excluído.', 'success');
      carregar();
    } catch { toast('Erro ao excluir.', 'error'); }
  }

  // forcar precisa ser exatamente true — antes o botão usava onClick={cobrar},
  // e o evento do clique (truthy) virava forcar=true em TODO clique, pulando o
  // aviso de "já gerada" e recriando a cobrança (boleto duplicado).
  async function cobrar(forcarParam = false) {
    const forcar = forcarParam === true;
    setCobrando(true); setResultadoCobrar(null); setPodeReprocessar(false);
    try {
      const r = await api.post(`/contratos/${modalCobrar.id}/cobrar`, {
        mes: cobrarMes,
        ano: cobrarAno,
        gerar_boleto: cobrarBoleto,
        emitir_nfse: cobrarNfse,
        ...(forcar ? { forcar: true } : {}),
      });
      setResultadoCobrar(r.data);
      if (r.data?.erros?.length) {
        toast('Cobrança registrada, mas com pendências — veja os detalhes.', 'error');
      } else {
        toast(forcar ? 'Cobrança reprocessada!' : 'Cobrança gerada!', 'success');
      }
      carregar();
    } catch (e) {
      const status = e.response?.status;
      const data   = e.response?.data || {};
      const msg    = data.erro || 'Erro ao gerar cobrança.';
      toast(msg, 'error');
      // Se 409 com pode_reprocessar, habilita botão de reprocessar
      if (status === 409 && data.pode_reprocessar) {
        setPodeReprocessar(true);
      }
      setResultadoCobrar({ erro: msg });
    }
    setCobrando(false);
  }

  async function verHistorico(c) {
    setModalHistorico(c);
    setHistoricoExpandido(null);
    try {
      const r = await api.get(`/contratos/${c.id}/historico`);
      setHistorico(r.data);
    } catch { setHistorico([]); }
  }

  async function reenviarEmail(historicoId) {
    if (!modalHistorico) return;
    setReenviandoId(historicoId);
    try {
      const r = await api.post(`/contratos/${modalHistorico.id}/reenviar-email`, { historico_id: historicoId });
      const atualizado = r.data?.historico;
      if (atualizado) {
        setHistorico(prev => prev.map(h => (h.id === historicoId ? atualizado : h)));
      }
      if (r.data?.ok) {
        toast('Email reenviado!', 'success');
      } else {
        toast(r.data?.erro || 'Não foi possível reenviar.', 'error');
      }
    } catch (e) {
      toast(e.response?.data?.erro || 'Erro ao reenviar email.', 'error');
    }
    setReenviandoId(null);
  }

  const filtrados = contratos.filter(c => {
    const matchBusca = !busca ||
      (c.nome || '').toLowerCase().includes(busca.toLowerCase()) ||
      (c.cliente_nome || '').toLowerCase().includes(busca.toLowerCase());
    if (filtroAtivo === 'ativo')   return matchBusca && c.ativo;
    if (filtroAtivo === 'inativo') return matchBusca && !c.ativo;
    return matchBusca;
  });

  const totalAtivos = contratos.filter(c => c.ativo).reduce((s, c) => s + parseFloat(c.valor || 0), 0);
  const comCobranca = contratos.filter(c => c.ativo && c.cobranca_ativa).length;

  const kpis = [
    { label: 'Total contratos', valor: contratos.length, icone: '📄' },
    { label: 'Contratos ativos', valor: contratos.filter(c => c.ativo).length, icone: '✅' },
    { label: 'Receita mensal', valor: fmt(totalAtivos), icone: '💰' },
    { label: 'Com cobrança auto', valor: comCobranca, icone: '🔄' },
  ];

  // Valida se NFS-e pode ser emitida (campos obrigatórios preenchidos)
  function validarNfse(c) {
    const avisos = [];
    if (!c.cliente_cnpj && !c.cliente_cpf) avisos.push('CPF/CNPJ do cliente não cadastrado');
    if (nfsePadroes.nfse_nacional) {
      // Ambiente Nacional: precisa do código de tributação nacional (contrato ou padrão da empresa)
      if (!c.codigo_tributacao_nacional && !nfsePadroes.codigo_tributacao_nacional_padrao) {
        avisos.push('Código de Tributação Nacional não configurado (contrato ou padrão da empresa)');
      }
    } else if (!c.item_lista_servico) {
      avisos.push('Código LC 116 não configurado');
    }
    return avisos;
  }

  function abrirPadroes() {
    setFormPadroes({ ...nfsePadroes });
    setModalPadroes(true);
  }

  async function salvarPadroes() {
    const codTrib = soDigitos(formPadroes.codigo_tributacao_nacional_padrao);
    if (codTrib && codTrib.length !== 6) {
      toast('O Código de Tributação Nacional deve ter 6 dígitos (ex: 010701).', 'error');
      return;
    }
    setSalvandoPadroes(true);
    try {
      const r = await api.put('/notas/fiscal/nfse-padroes', {
        codigo_tributacao_nacional_padrao: codTrib,
        codigo_nbs_padrao: (formPadroes.codigo_nbs_padrao || '').trim(),
      });
      setNfsePadroes(p => ({
        ...p,
        codigo_tributacao_nacional_padrao: r.data?.codigo_tributacao_nacional_padrao ?? codTrib,
        codigo_nbs_padrao: r.data?.codigo_nbs_padrao ?? '',
      }));
      toast('Padrões de NFS-e salvos!', 'success');
      setModalPadroes(false);
    } catch (e) {
      toast(e.response?.data?.erro || 'Erro ao salvar padrões de NFS-e.', 'error');
    }
    setSalvandoPadroes(false);
  }

  return (
    <div style={{ padding: '0 0 40px' }}>
      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24, flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: C.navy }}>📄 Contratos</h1>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: C.muted }}>Contratos recorrentes com cobrança e NFS-e automáticos</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {ehGestor && (
            <button onClick={abrirPadroes} style={btnSec} title="Códigos padrão usados na NFS-e Nacional quando o contrato não tem código próprio">
              ⚙️ Padrões NFS-e
            </button>
          )}
          <button onClick={abrirNovo} style={btnPrimary}>+ Novo Contrato</button>
        </div>
      </div>

      {/* Aviso: empresa no Ambiente Nacional sem código padrão */}
      {nfsePadroes.nfse_nacional && !nfsePadroes.codigo_tributacao_nacional_padrao && (
        <div style={{ background: '#fff8f0', border: '1px solid #fed7aa', borderRadius: 10, padding: '10px 14px', marginBottom: 18, fontSize: 13, color: '#9a3412' }}>
          ℹ️ Sua empresa emite NFS-e no <strong>Ambiente Nacional</strong>. Cadastre o <strong>Código de Tributação Nacional</strong> em cada contrato
          {ehGestor ? <> ou um padrão em <strong>⚙️ Padrões NFS-e</strong></> : ' (ou peça ao gestor para cadastrar um padrão)'} — o código municipal/LC 116 sozinho não é aceito.
        </div>
      )}

      {/* KPIs */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 14, marginBottom: 24 }}>
        {kpis.map(k => (
          <div key={k.label} style={{ ...card, textAlign: 'center', padding: '16px 12px' }}>
            <div style={{ fontSize: 26, marginBottom: 6 }}>{k.icone}</div>
            <div style={{ fontSize: 20, fontWeight: 800, color: C.navy }}>{k.valor}</div>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>{k.label}</div>
          </div>
        ))}
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 10, marginBottom: 18, flexWrap: 'wrap', alignItems: 'center' }}>
        <input value={busca} onChange={e => setBusca(e.target.value)}
          placeholder="🔍 Buscar contrato ou cliente..."
          style={{ ...inp, width: 280 }} />
        {['todos', 'ativo', 'inativo'].map(f => (
          <button key={f} onClick={() => setFiltroAtivo(f)} style={{
            ...btnSec, padding: '8px 14px', fontSize: 13,
            background: filtroAtivo === f ? C.navy : '#f5f7fa',
            color: filtroAtivo === f ? '#fff' : C.text,
            border: `1px solid ${filtroAtivo === f ? C.navy : C.border}`,
          }}>
            {f === 'todos' ? 'Todos' : f === 'ativo' ? '✅ Ativos' : '○ Inativos'}
          </button>
        ))}
      </div>

      {/* Lista */}
      {carregando ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>Carregando...</div>
      ) : filtrados.length === 0 ? (
        <div style={{ textAlign: 'center', padding: 60, color: C.muted }}>
          <div style={{ fontSize: 40, marginBottom: 12 }}>📄</div>
          <p>Nenhum contrato encontrado.</p>
          <button onClick={abrirNovo} style={{ ...btnPrimary, marginTop: 12 }}>+ Criar primeiro contrato</button>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          {filtrados.map(c => (
            <div key={c.id} style={{ ...card, borderLeft: `4px solid ${c.ativo ? (c.cobranca_ativa ? C.orange : C.success) : C.border}` }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 10 }}>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, fontSize: 15, color: C.navy }}>{c.nome}</span>
                    <Badge ok={c.ativo} />
                    {c.cobranca_ativa && (
                      <span style={{ padding: '2px 8px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: '#fff3eb', color: C.orange }}>
                        🔄 Auto
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: 13, color: C.muted, display: 'flex', flexWrap: 'wrap', gap: 14 }}>
                    {c.cliente_nome && <span>👤 {c.cliente_nome}</span>}
                    {c.data_inicio  && <span>📅 Desde {fmtData(c.data_inicio)}</span>}
                    {c.cobranca_ativa && <span>📅 Vence dia {c.dia_vencimento}</span>}
                    {c.emitir_nfse  && <span>🧾 NFS-e auto</span>}
                  </div>
                </div>
                <div style={{ textAlign: 'right', flexShrink: 0 }}>
                  <div style={{ fontSize: 20, fontWeight: 800, color: C.navy }}>{fmt(c.valor)}</div>
                  <div style={{ fontSize: 11, color: C.muted }}>por mês</div>
                </div>
              </div>

              {c.historico_recente?.length > 0 && (
                <div style={{ marginTop: 12, display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                  {c.historico_recente.slice(0, 4).map(h => (
                    <div key={h.id} style={{ background: '#f8fafc', border: `1px solid ${C.border}`, borderRadius: 6, padding: '4px 8px', fontSize: 11 }}>
                      <span style={{ color: C.muted }}>{MESES[h.mes - 1]}/{h.ano} </span>
                      <StatusBadge status={h.status} />
                    </div>
                  ))}
                </div>
              )}

              <div style={{ marginTop: 14, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                <button onClick={() => abrirEditar(c)} style={{ ...btnSec, padding: '6px 14px', fontSize: 12 }}>✏️ Editar</button>
                <button onClick={() => abrirCobrar(c)} style={{ ...btnSec, padding: '6px 14px', fontSize: 12, color: C.orange, borderColor: C.orange }}>
                  💳 Cobrar
                </button>
                <button onClick={() => verHistorico(c)} style={{ ...btnSec, padding: '6px 14px', fontSize: 12 }}>📋 Histórico</button>
                <button onClick={() => excluir(c)} style={{ ...btnSec, padding: '6px 14px', fontSize: 12, color: '#dc2626', borderColor: '#fca5a5' }}>🗑 Excluir</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Modal Novo/Editar ──────────────────────────────────────────────── */}
      {modal !== null && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => e.target === e.currentTarget && setModal(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 600, maxHeight: '92vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h2 style={{ margin: 0, color: C.navy }}>{modal === 'novo' ? '+ Novo Contrato' : '✏️ Editar Contrato'}</h2>
              <button onClick={() => setModal(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: C.muted }}>×</button>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={lbl}>Nome do contrato *</label>
                <input style={inp} value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} placeholder="Ex: Mensalidade Suporte TI" />
              </div>

              <div>
                <label style={lbl}>Valor mensal (R$) *</label>
                <input style={inp} type="number" min="0" step="0.01" value={form.valor} onChange={e => setForm(p => ({ ...p, valor: e.target.value }))} placeholder="0,00" />
              </div>

              <div>
                <label style={lbl}>Cliente</label>
                <select style={inp} value={form.cliente_id} onChange={e => setForm(p => ({ ...p, cliente_id: e.target.value }))}>
                  <option value="">— Sem cliente vinculado —</option>
                  {clientes.map(cl => (
                    <option key={cl.id} value={cl.id}>
                      {cl.cliente_apelido ? `${cl.nome} (${cl.cliente_apelido})` : cl.nome}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label style={lbl}>Data de início</label>
                <input style={inp} type="date" value={form.data_inicio} onChange={e => setForm(p => ({ ...p, data_inicio: e.target.value }))} />
              </div>

              <hr style={{ border: 'none', borderTop: `1px solid ${C.border}`, margin: '4px 0' }} />
              <div style={{ fontSize: 12, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.5px' }}>Cobrança</div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div>
                  <label style={lbl}>Dia de geração da cobrança</label>
                  <input style={inp} type="number" min="1" max="28" value={form.dia_geracao}
                    onChange={e => setForm(p => ({ ...p, dia_geracao: e.target.value }))} />
                  <span style={{ fontSize: 11, color: C.muted, marginTop: 3, display: 'block' }}>Dia do mês em que o boleto/NFS-e é gerado</span>
                </div>
                <div>
                  <label style={lbl}>Dia de vencimento</label>
                  <input style={inp} type="number" min="1" max="28" value={form.dia_vencimento}
                    onChange={e => setForm(p => ({ ...p, dia_vencimento: e.target.value }))} />
                  <span style={{ fontSize: 11, color: C.muted, marginTop: 3, display: 'block' }}>Dia do mês em que o boleto vence</span>
                </div>
              </div>

              {/* Toggle cobrança automática + boleto — independentes da NFS-e */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <Toggle value={form.cobranca_ativa} onChange={() => setForm(p => ({ ...p, cobranca_ativa: !p.cobranca_ativa }))} cor={C.orange} />
                <span style={{ fontSize: 13, color: C.text }}>Ativar cobrança automática</span>
              </label>

              {form.cobranca_ativa && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginLeft: 8 }}>
                  <Toggle value={form.gerar_boleto} onChange={() => setForm(p => ({ ...p, gerar_boleto: !p.gerar_boleto }))} />
                  <span style={{ fontSize: 13, color: C.text }}>🏦 Gerar boleto ASAAS automaticamente</span>
                </label>
              )}

              <hr style={{ border: 'none', borderTop: `1px solid ${C.border}`, margin: '2px 0' }} />

              {/* NFS-e — independente da cobrança automática */}
              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <Toggle value={form.emitir_nfse} onChange={() => setForm(p => ({ ...p, emitir_nfse: !p.emitir_nfse }))} />
                <div>
                  <div style={{ fontSize: 13, color: C.text, fontWeight: 600 }}>🧾 Emitir NFS-e</div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 1 }}>
                    {form.cobranca_ativa ? 'Emite automaticamente junto com a cobrança' : 'Pode emitir manualmente pelo botão Cobrar'}
                  </div>
                </div>
              </label>

              {form.emitir_nfse && (
                <div style={{ background: '#f0fdf4', border: `1px solid #86efac`, borderRadius: 10, padding: 14, display: 'flex', flexDirection: 'column', gap: 12 }}>
                  <div style={{ fontSize: 12, fontWeight: 700, color: '#15803d', textTransform: 'uppercase', letterSpacing: '.5px' }}>Configuração NFS-e</div>
                  {nfsePadroes.nfse_nacional && (
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 12 }}>
                      <div>
                        <label style={lbl}>Código de Tributação Nacional</label>
                        <input style={inp} value={form.codigo_tributacao_nacional} inputMode="numeric" maxLength={9}
                          onChange={e => setForm(p => ({ ...p, codigo_tributacao_nacional: e.target.value }))}
                          placeholder={nfsePadroes.codigo_tributacao_nacional_padrao ? `Padrão: ${nfsePadroes.codigo_tributacao_nacional_padrao}` : 'Ex: 010701'} />
                        <span style={{ fontSize: 11, color: C.muted, marginTop: 3, display: 'block' }}>6 dígitos. Vazio = usa o padrão da empresa</span>
                      </div>
                      <div>
                        <label style={lbl}>Código NBS</label>
                        <input style={inp} value={form.codigo_nbs} maxLength={20}
                          onChange={e => setForm(p => ({ ...p, codigo_nbs: e.target.value }))}
                          placeholder={nfsePadroes.codigo_nbs_padrao ? `Padrão: ${nfsePadroes.codigo_nbs_padrao}` : 'Ex: 1.1506.10.00'} />
                        <span style={{ fontSize: 11, color: C.muted, marginTop: 3, display: 'block' }}>Confirme com a contabilidade</span>
                      </div>
                    </div>
                  )}
                  <div>
                    <label style={lbl}>Código do serviço LC 116{nfsePadroes.nfse_nacional ? ' (opcional)' : ' *'}</label>
                    <input style={inp} value={form.item_lista_servico}
                      onChange={e => setForm(p => ({ ...p, item_lista_servico: e.target.value }))}
                      placeholder="Ex: 01.01, 01.07, 14.01..." />
                    <span style={{ fontSize: 11, color: C.muted, marginTop: 3, display: 'block' }}>
                      {nfsePadroes.nfse_nacional
                        ? 'No Ambiente Nacional vale o Código de Tributação Nacional acima'
                        : 'Consulte a lista LC 116/2003 do seu município'}
                    </span>
                  </div>
                  <div>
                    <label style={lbl}>Discriminação do serviço</label>
                    <textarea style={{ ...inp, resize: 'vertical', height: 70 }} value={form.discriminacao}
                      onChange={e => setForm(p => ({ ...p, discriminacao: e.target.value }))}
                      placeholder="Ex: Prestação de serviços de suporte técnico e manutenção de sistemas de TI conforme contrato" />
                    <span style={{ fontSize: 11, color: C.muted, marginTop: 3, display: 'block' }}>Texto que aparece na nota fiscal</span>
                  </div>
                </div>
              )}

              <div>
                <label style={lbl}>Observação interna</label>
                <textarea style={{ ...inp, resize: 'vertical', height: 60 }} value={form.observacao} onChange={e => setForm(p => ({ ...p, observacao: e.target.value }))} placeholder="Anotações internas sobre o contrato" />
              </div>

              <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
                <Toggle value={form.ativo} onChange={() => setForm(p => ({ ...p, ativo: !p.ativo }))} />
                <span style={{ fontSize: 13, color: C.text }}>Contrato ativo</span>
              </label>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setModal(null)} style={btnSec}>Cancelar</button>
              <button onClick={salvar} disabled={salvando} style={{ ...btnPrimary, opacity: salvando ? 0.7 : 1 }}>
                {salvando ? 'Salvando...' : 'Salvar contrato'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Cobrar ──────────────────────────────────────────────────── */}
      {modalCobrar && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => e.target === e.currentTarget && !cobrando && (setModalCobrar(null), setResultadoCobrar(null))}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>

            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <div style={{ fontWeight: 700, fontSize: 18, color: C.navy }}>💳 Gerar Cobrança</div>
              {!cobrando && (
                <button onClick={() => { setModalCobrar(null); setResultadoCobrar(null); }}
                  style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.muted }}>✕</button>
              )}
            </div>

            {/* ── Sucesso ── */}
            {resultadoCobrar?.ok && (
              <div>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ fontSize: 48, marginBottom: 10 }}>{resultadoCobrar.erros?.length ? '⚠️' : '✅'}</div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: resultadoCobrar.erros?.length ? '#92400e' : '#155724' }}>
                    {resultadoCobrar.erros?.length
                      ? 'Cobrança registrada com pendências'
                      : resultadoCobrar.reprocessado ? 'Cobrança reprocessada com sucesso!' : 'Cobrança gerada com sucesso!'}
                  </div>
                  <div style={{ fontSize: 13, color: C.muted, marginTop: 6 }}>
                    {MESES_FULL[cobrarMes - 1]}/{cobrarAno} · {fmt(modalCobrar.valor)} · Vence dia {modalCobrar.dia_vencimento}
                  </div>
                </div>
                {resultadoCobrar.asaas_url && (
                  <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 10, padding: 14, marginBottom: 12 }}>
                    <div style={{ fontWeight: 700, fontSize: 13, color: '#0369a1', marginBottom: 8 }}>🏦 Boleto ASAAS gerado</div>
                    <a href={resultadoCobrar.asaas_url} target="_blank" rel="noreferrer"
                      style={{ display: 'block', textAlign: 'center', padding: '10px', background: '#0369a1', color: 'white', borderRadius: 8, fontWeight: 700, fontSize: 13, textDecoration: 'none' }}>
                      Abrir boleto ↗
                    </a>
                  </div>
                )}
                {resultadoCobrar.avisos?.length > 0 && (
                  <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#0369a1', marginBottom: 12 }}>
                    {resultadoCobrar.avisos.map((a, i) => <div key={i}>ℹ️ {a}</div>)}
                  </div>
                )}
                {resultadoCobrar.erros?.length > 0 && (
                  <div style={{ background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#856404', marginBottom: 12 }}>
                    {resultadoCobrar.erros.map((e, i) => <div key={i}>⚠️ {e}</div>)}
                  </div>
                )}
                <button onClick={() => { setModalCobrar(null); setResultadoCobrar(null); }}
                  style={{ width: '100%', padding: '12px', background: '#f0f3f7', color: C.text, border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                  Fechar
                </button>
              </div>
            )}

            {/* ── Erro ── */}
            {resultadoCobrar?.erro && (
              <div>
                <div style={{ textAlign: 'center', marginBottom: 20 }}>
                  <div style={{ fontSize: 48, marginBottom: 10 }}>❌</div>
                  <div style={{ fontWeight: 700, fontSize: 15, color: '#991b1b' }}>{resultadoCobrar.erro}</div>
                  {podeReprocessar && (
                    <div style={{ fontSize: 12, color: C.muted, marginTop: 8, background: '#fff8f0', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 12px' }}>
                      ℹ️ Já existe um registro para este mês. <strong>Reprocessar</strong> refaz só o que falhou:
                      o boleto já gerado é <strong>reaproveitado</strong> (não cria outro) e a NFS-e só é reemitida se a anterior foi rejeitada.
                    </div>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 10 }}>
                  {podeReprocessar ? (
                    <button onClick={() => cobrar(true)}
                      style={{ flex: 1, padding: '12px', background: '#dc2626', color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                      🔄 Reprocessar
                    </button>
                  ) : (
                    <button onClick={() => { setResultadoCobrar(null); setPodeReprocessar(false); }}
                      style={{ flex: 1, padding: '12px', background: C.orange, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
                      Tentar novamente
                    </button>
                  )}
                  <button onClick={() => { setModalCobrar(null); setResultadoCobrar(null); setPodeReprocessar(false); }}
                    style={{ flex: 1, padding: '12px', background: '#f0f3f7', color: C.text, border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                    Fechar
                  </button>
                </div>
              </div>
            )}

            {/* ── Formulário ── */}
            {!resultadoCobrar && (
              <div>
                {/* Card resumo */}
                <div style={{ background: '#f7f8fa', borderRadius: 10, padding: '14px 16px', marginBottom: 20 }}>
                  <div style={{ fontSize: 13, color: C.muted, marginBottom: 4 }}>
                    {modalCobrar.cliente_nome ? `👤 ${modalCobrar.cliente_nome}` : '📄 Sem cliente vinculado'}
                  </div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: C.navy }}>{modalCobrar.nome}</div>
                  <div style={{ fontSize: 24, fontWeight: 800, color: C.orange, marginTop: 6 }}>{fmt(modalCobrar.valor)}</div>
                </div>

                {/* Competência */}
                <div style={{ marginBottom: 16 }}>
                  <label style={lbl}>Competência</label>
                  <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 10 }}>
                    <select style={inp} value={cobrarMes} onChange={e => setCobrarMes(Number(e.target.value))}>
                      {MESES_FULL.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
                    </select>
                    <input style={inp} type="number" min="2024" max="2030" value={cobrarAno}
                      onChange={e => setCobrarAno(Number(e.target.value))} />
                  </div>
                </div>

                {/* Preview vencimento */}
                <div style={{ background: '#f8fafc', border: `1px solid ${C.border}`, borderRadius: 8, padding: '12px 14px', marginBottom: 20, fontSize: 13 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: C.muted }}>📅 Data de vencimento</span>
                    <strong style={{ color: C.navy }}>{calcVencimento(cobrarMes, cobrarAno, modalCobrar.dia_vencimento)}</strong>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 6 }}>
                    <span style={{ color: C.muted }}>💰 Valor da cobrança</span>
                    <strong style={{ color: C.orange }}>{fmt(modalCobrar.valor)}</strong>
                  </div>
                </div>

                {/* O que será gerado — toggles editáveis */}
                <div style={{ marginBottom: 20 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 10 }}>
                    O que será gerado
                  </div>

                  {/* Boleto */}
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', padding: '10px 12px', borderRadius: 8, border: `1.5px solid ${cobrarBoleto ? '#bae6fd' : C.border}`, background: cobrarBoleto ? '#f0f9ff' : '#fafafa', marginBottom: 8, transition: 'all .15s' }}>
                    <Toggle value={cobrarBoleto} onChange={() => setCobrarBoleto(p => !p)} cor="#0369a1" />
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: cobrarBoleto ? '#0369a1' : C.muted }}>🏦 Boleto ASAAS</div>
                      {cobrarBoleto ? (
                        <div style={{ fontSize: 11, color: '#0369a1', marginTop: 2 }}>
                          {modalCobrar.cliente_cnpj || modalCobrar.cliente_cpf
                            ? `Cliente: ${modalCobrar.cliente_nome}`
                            : <span style={{ color: '#b45309' }}>⚠️ Cliente sem CPF/CNPJ — boleto pode falhar</span>
                          }
                        </div>
                      ) : (
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Desativado para esta cobrança</div>
                      )}
                    </div>
                  </label>

                  {/* NFS-e */}
                  <label style={{ display: 'flex', alignItems: 'flex-start', gap: 12, cursor: 'pointer', padding: '10px 12px', borderRadius: 8, border: `1.5px solid ${cobrarNfse ? '#86efac' : C.border}`, background: cobrarNfse ? '#f0fdf4' : '#fafafa', transition: 'all .15s' }}>
                    <Toggle value={cobrarNfse} onChange={() => setCobrarNfse(p => !p)} cor={C.success} />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: cobrarNfse ? '#15803d' : C.muted }}>🧾 NFS-e</div>
                      {cobrarNfse ? (() => {
                        const avisos = validarNfse(modalCobrar);
                        return avisos.length > 0 ? (
                          <div style={{ fontSize: 11, color: '#b45309', marginTop: 2 }}>
                            ⚠️ {avisos.join(' · ')}
                          </div>
                        ) : (
                          <div style={{ fontSize: 11, color: '#15803d', marginTop: 2 }}>
                            {nfsePadroes.nfse_nacional
                              ? <span>Cód. nacional: {modalCobrar.codigo_tributacao_nacional || `${nfsePadroes.codigo_tributacao_nacional_padrao} (padrão)`}</span>
                              : modalCobrar.item_lista_servico && <span>Código: {modalCobrar.item_lista_servico}</span>}
                            {modalCobrar.discriminacao && <span> · {modalCobrar.discriminacao.slice(0, 40)}{modalCobrar.discriminacao.length > 40 ? '…' : ''}</span>}
                          </div>
                        );
                      })() : (
                        <div style={{ fontSize: 11, color: C.muted, marginTop: 2 }}>Desativado para esta cobrança</div>
                      )}
                    </div>
                  </label>

                  {/* Aviso se nenhum está ativo */}
                  {!cobrarBoleto && !cobrarNfse && (
                    <div style={{ marginTop: 8, padding: '8px 12px', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 8, fontSize: 12, color: '#856404' }}>
                      ⚠️ Nenhuma ação selecionada — apenas o registro histórico será criado.
                    </div>
                  )}
                </div>

                {/* Botões */}
                <div style={{ display: 'flex', gap: 10 }}>
                  <button onClick={() => { setModalCobrar(null); setResultadoCobrar(null); }}
                    style={{ flex: 1, padding: '12px', background: '#f0f3f7', color: C.text, border: 'none', borderRadius: 8, fontWeight: 600, fontSize: 14, cursor: 'pointer' }}>
                    Cancelar
                  </button>
                  <button onClick={() => cobrar(false)} disabled={cobrando}
                    style={{ flex: 2, padding: '12px', background: cobrando ? C.muted : C.orange, color: 'white', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: cobrando ? 'not-allowed' : 'pointer', opacity: cobrando ? 0.8 : 1 }}>
                    {cobrando ? '⏳ Gerando...' : '💳 Gerar cobrança'}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Modal Padrões NFS-e (somente gestor) ───────────────────────────── */}
      {modalPadroes && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => e.target === e.currentTarget && !salvandoPadroes && setModalPadroes(false)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 480, boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <h2 style={{ margin: 0, color: C.navy, fontSize: 18 }}>⚙️ Padrões NFS-e</h2>
              <button onClick={() => setModalPadroes(false)} disabled={salvandoPadroes}
                style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: C.muted }}>×</button>
            </div>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
              Usados na NFS-e do <strong>Ambiente Nacional</strong> quando o contrato (ou o serviço do orçamento) não tem código próprio.
              {!nfsePadroes.nfse_nacional && <><br /><span style={{ color: '#b45309' }}>Sua empresa ainda emite no modelo municipal — estes padrões só valem após ativar o Ambiente Nacional.</span></>}
            </p>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={lbl}>Código de Tributação Nacional</label>
                <input style={inp} value={formPadroes.codigo_tributacao_nacional_padrao || ''} inputMode="numeric" maxLength={9}
                  onChange={e => setFormPadroes(p => ({ ...p, codigo_tributacao_nacional_padrao: e.target.value }))}
                  placeholder="Ex: 010701" />
                <span style={{ fontSize: 11, color: C.muted, marginTop: 3, display: 'block' }}>6 dígitos — confirme com a contabilidade</span>
              </div>
              <div>
                <label style={lbl}>Código NBS</label>
                <input style={inp} value={formPadroes.codigo_nbs_padrao || ''} maxLength={20}
                  onChange={e => setFormPadroes(p => ({ ...p, codigo_nbs_padrao: e.target.value }))}
                  placeholder="Ex: 1.1506.10.00" />
                <span style={{ fontSize: 11, color: C.muted, marginTop: 3, display: 'block' }}>Opcional — só é enviado quando preenchido</span>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 24 }}>
              <button onClick={() => setModalPadroes(false)} disabled={salvandoPadroes} style={btnSec}>Cancelar</button>
              <button onClick={salvarPadroes} disabled={salvandoPadroes} style={{ ...btnPrimary, opacity: salvandoPadroes ? 0.7 : 1 }}>
                {salvandoPadroes ? 'Salvando...' : 'Salvar padrões'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal Histórico ───────────────────────────────────────────────── */}
      {modalHistorico && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 500, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={e => e.target === e.currentTarget && setModalHistorico(null)}>
          <div style={{ background: '#fff', borderRadius: 16, padding: 28, width: '100%', maxWidth: 560, maxHeight: '80vh', overflowY: 'auto', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h2 style={{ margin: 0, color: C.navy }}>📋 Histórico</h2>
              <button onClick={() => setModalHistorico(null)} style={{ background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: C.muted }}>×</button>
            </div>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: C.muted, fontWeight: 600 }}>{modalHistorico.nome}</p>

            {historico.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: C.muted }}>Nenhuma cobrança gerada ainda.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                {historico.map(h => {
                  const aberto = historicoExpandido === h.id;
                  const reenviando = reenviandoId === h.id;
                  return (
                    <div key={h.id} style={{ background: '#f8fafc', border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden' }}>
                      {/* Linha principal — clicável para expandir detalhes do email */}
                      <div
                        onClick={() => setHistoricoExpandido(aberto ? null : h.id)}
                        style={{ padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8, cursor: 'pointer' }}
                      >
                        <div>
                          <span style={{ fontWeight: 700, color: C.navy }}>{MESES[h.mes - 1]}/{h.ano}</span>
                          <span style={{ fontSize: 12, color: C.muted, marginLeft: 10 }}>
                            Vence {fmtData(h.vencimento)}
                          </span>
                          <span style={{ fontSize: 11, color: C.muted, marginLeft: 8 }}>{aberto ? '▲' : '▼'}</span>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                          <span style={{ fontWeight: 700, color: C.navy }}>{fmt(h.valor)}</span>
                          <StatusBadge status={h.status} />
                          <EmailStatusBadge status={h.email_status} />
                          {h.asaas_cobranca_url && (
                            <a href={h.asaas_cobranca_url} target="_blank" rel="noreferrer"
                              onClick={e => e.stopPropagation()}
                              style={{ fontSize: 12, color: C.orange, fontWeight: 700 }}>🔗 Boleto</a>
                          )}
                        </div>
                      </div>

                      {h.erro_detalhe && (
                        <div style={{ margin: '0 14px 10px', fontSize: 11, color: '#dc2626', background: '#fee2e2', padding: '4px 8px', borderRadius: 4 }}>
                          {h.erro_detalhe}
                        </div>
                      )}

                      {/* Painel expandido — detalhes do envio de email */}
                      {aberto && (
                        <div style={{ padding: '12px 14px', borderTop: `1px solid ${C.border}`, background: '#fff' }}>
                          <div style={{ fontSize: 11, fontWeight: 700, color: C.muted, textTransform: 'uppercase', letterSpacing: '.5px', marginBottom: 8 }}>
                            Envio de email
                          </div>

                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, fontSize: 13, marginBottom: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: C.muted }}>Status</span>
                              <EmailStatusBadge status={h.email_status} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: C.muted }}>Enviado em</span>
                              <strong style={{ color: C.navy }}>{fmtDataHora(h.email_enviado_em)}</strong>
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 10 }}>
                              <span style={{ color: C.muted, flexShrink: 0 }}>Destinatários</span>
                              <span style={{ color: C.navy, textAlign: 'right', wordBreak: 'break-word' }}>
                                {h.email_destinatarios?.length > 0 ? h.email_destinatarios.join(', ') : '—'}
                              </span>
                            </div>
                            {h.email_tentativas > 0 && (
                              <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                                <span style={{ color: C.muted }}>Tentativas de envio</span>
                                <strong style={{ color: C.navy }}>{h.email_tentativas}</strong>
                              </div>
                            )}
                            {h.email_erro_detalhe && (
                              <div style={{ fontSize: 11, color: '#dc2626', background: '#fee2e2', padding: '6px 8px', borderRadius: 4, marginTop: 2 }}>
                                {h.email_erro_detalhe}
                              </div>
                            )}
                          </div>

                          <button
                            onClick={() => reenviarEmail(h.id)}
                            disabled={reenviando}
                            style={{
                              ...btnSec, padding: '8px 16px', fontSize: 12, width: '100%',
                              color: reenviando ? C.muted : C.orange,
                              borderColor: reenviando ? C.border : C.orange,
                              cursor: reenviando ? 'not-allowed' : 'pointer',
                            }}
                          >
                            {reenviando ? '⏳ Reenviando...' : '📤 Reenviar email (boleto + NFS-e)'}
                          </button>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
